/**
 * Billing API services.
 * Customer endpoints live under /v1/billing/*; admin endpoints under
 * /admin/billing/* (SUPER_ADMIN | FINANCE_ADMIN only).
 */

import { getApiClient } from '../api-client-factory';
import { API_ENDPOINTS } from '../api-endpoints';
import type { PaginatedResponse } from '../api-types';
import { resolveOrgPath } from '../org-path';
import type {
  SubscriptionPlanDto,
  SubscriptionDto,
  InvoiceDto,
  BillingOverviewDto,
  CreateCheckoutSessionResponse,
  EntitlementCheckResult,
  EntitlementDto,
  CouponDto,
  BillingInterval,
  BillingSubjectType,
  PlanContext,
  SubscriptionStatus,
  BillingReportSummary,
  CohortRetentionRow,
  DelinquentAgingBucket,
  DunningConfig,
  GstSettings,
  PlanLimits,
} from '@welynk/types';

// ─── Customer-facing ──────────────────────────────────────────────────────────

export interface CheckoutRequest {
  planId: string;
  billingInterval: BillingInterval;
  organizationId?: string;
  seatCount?: number;
  couponCode?: string;
  /** Relative path to redirect to after Stripe Checkout succeeds/cancels (defaults to the current page) */
  successPath?: string;
  cancelPath?: string;
}

export interface ProrationPreview {
  /** minor units */
  amountDue: number;
  currency: string;
  lines: Array<{ description: string; amount: number }>;
}

export class BillingApiService {
  private static get client() {
    return getApiClient();
  }

  static async getPlans(context?: PlanContext): Promise<SubscriptionPlanDto[]> {
    const r = await this.client.get(API_ENDPOINTS.BILLING.PLANS, { params: { context } });
    return r.data.data;
  }

  static async getOverview(organizationId?: string): Promise<BillingOverviewDto> {
    const r = await this.client.get(API_ENDPOINTS.BILLING.OVERVIEW, {
      params: { organizationId },
    });
    return r.data.data;
  }

  static async getInvoices(params: {
    page?: number;
    pageSize?: number;
    organizationId?: string;
  }): Promise<PaginatedResponse<InvoiceDto>> {
    const r = await this.client.get(API_ENDPOINTS.BILLING.INVOICES, { params });
    return r.data.data;
  }

  static async createCheckout(body: CheckoutRequest): Promise<CreateCheckoutSessionResponse> {
    const { successPath, cancelPath, ...rest } = body;
    // Workspace paths come back to this tab's organization (/college/<slug>/…).
    const successBase = `${window.location.origin}${
      successPath ? resolveOrgPath(successPath) : window.location.pathname
    }`;
    // Stripe replaces this literal placeholder with the real session id on
    // redirect — the success page uses it to reconcile activation
    // synchronously instead of waiting on a webhook.
    const sessionIdParam = 'session_id={CHECKOUT_SESSION_ID}';
    const successUrl = `${successBase}${successBase.includes('?') ? '&' : '?'}${sessionIdParam}`;
    const r = await this.client.post(API_ENDPOINTS.BILLING.CHECKOUT, {
      ...rest,
      successUrl,
      cancelUrl: `${window.location.origin}${
        cancelPath ? resolveOrgPath(cancelPath) : window.location.pathname
      }?checkout=canceled`,
    });
    return r.data.data;
  }

  /** Synchronous activation fallback — see CheckoutSuccessContent */
  static async reconcileCheckout(
    sessionId: string,
    organizationId?: string
  ): Promise<SubscriptionDto> {
    const r = await this.client.post(API_ENDPOINTS.BILLING.RECONCILE, {
      sessionId,
      organizationId,
    });
    return r.data.data;
  }

  static async cancelSubscription(
    organizationId?: string,
    immediate = false
  ): Promise<SubscriptionDto> {
    const r = await this.client.post(API_ENDPOINTS.BILLING.CANCEL, { organizationId, immediate });
    return r.data.data;
  }

  static async resumeSubscription(organizationId?: string): Promise<SubscriptionDto> {
    const r = await this.client.post(API_ENDPOINTS.BILLING.RESUME, { organizationId });
    return r.data.data;
  }

  static async previewPlanChange(body: CheckoutRequest): Promise<ProrationPreview> {
    const r = await this.client.post(API_ENDPOINTS.BILLING.CHANGE_PLAN_PREVIEW, body);
    return r.data.data;
  }

  static async changePlan(body: CheckoutRequest): Promise<SubscriptionDto> {
    const r = await this.client.post(API_ENDPOINTS.BILLING.CHANGE_PLAN, body);
    return r.data.data;
  }

  static async checkEntitlement(
    featureKey: string,
    organizationId?: string
  ): Promise<EntitlementCheckResult> {
    const r = await this.client.get(API_ENDPOINTS.BILLING.ENTITLEMENT_CHECK, {
      params: { featureKey, organizationId },
    });
    return r.data.data;
  }

  static async previewCoupon(code: string, planId: string): Promise<CouponPreview> {
    const r = await this.client.get(API_ENDPOINTS.BILLING.COUPON_PREVIEW, {
      params: { code, planId },
    });
    return r.data.data;
  }

  /** Redirects the browser to the Stripe-hosted Customer Portal */
  static async createPortalSession(
    returnUrl: string,
    organizationId?: string
  ): Promise<CreateCheckoutSessionResponse> {
    const r = await this.client.post(API_ENDPOINTS.BILLING.PORTAL, { returnUrl, organizationId });
    return r.data.data;
  }
}

export interface CouponPreview {
  valid: boolean;
  message?: string;
  discountType?: 'PERCENT' | 'FIXED_AMOUNT';
  discountValue?: number;
  description?: string | null;
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface AdminSubscriptionRow extends SubscriptionDto {
  subjectLabel: string;
  subjectEmail: string | null;
}

export interface AdminSubscriptionDetail {
  subscription: SubscriptionDto;
  entitlements: EntitlementDto[];
  invoices: InvoiceDto[];
  auditLogs: BillingAuditLogRow[];
}

export interface BillingAuditLogRow {
  id: string;
  actorType: 'ADMIN' | 'SYSTEM' | 'CUSTOMER';
  actorId: string | null;
  actorEmail: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  reason: string | null;
  createdAt: string;
}

export interface AdminSubscriptionsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  planId?: string;
  status?: SubscriptionStatus;
  context?: PlanContext;
}

export class AdminBillingApiService {
  private static get client() {
    return getApiClient();
  }

  static async getStatus(): Promise<{ paymentMode: 'test' | 'live' | 'disabled' }> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_STATUS);
    return r.data.data;
  }

  // ── Subscriptions ───────────────────────────────────────────────────────────

  static async getSubscriptions(
    params?: AdminSubscriptionsParams
  ): Promise<PaginatedResponse<AdminSubscriptionRow>> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_SUBSCRIPTIONS, { params });
    return r.data.data;
  }

  static async getSubscriptionDetail(
    id: string,
    subjectType: BillingSubjectType
  ): Promise<AdminSubscriptionDetail> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_SUBSCRIPTION_DETAIL(id), {
      params: { subjectType },
    });
    return r.data.data;
  }

  static async previewPlanChange(
    id: string,
    body: {
      subjectType: BillingSubjectType;
      planId: string;
      billingInterval: BillingInterval;
      seatCount?: number;
    }
  ): Promise<ProrationPreview> {
    const r = await this.client.post(
      API_ENDPOINTS.ADMIN.BILLING_SUBSCRIPTION_CHANGE_PLAN_PREVIEW(id),
      body
    );
    return r.data.data;
  }

  static async changePlan(
    id: string,
    body: {
      subjectType: BillingSubjectType;
      planId: string;
      billingInterval: BillingInterval;
      seatCount?: number;
    }
  ): Promise<SubscriptionDto> {
    const r = await this.client.post(
      API_ENDPOINTS.ADMIN.BILLING_SUBSCRIPTION_CHANGE_PLAN(id),
      body
    );
    return r.data.data;
  }

  static async pauseSubscription(
    id: string,
    subjectType: BillingSubjectType,
    reason: string
  ): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_SUBSCRIPTION_PAUSE(id), {
      subjectType,
      reason,
    });
  }

  static async resumeSubscription(id: string, subjectType: BillingSubjectType): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_SUBSCRIPTION_RESUME(id), { subjectType });
  }

  static async cancelSubscription(
    id: string,
    body: {
      subjectType: BillingSubjectType;
      immediate: boolean;
      refundLastInvoice: boolean;
      reason: string;
    }
  ): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_SUBSCRIPTION_CANCEL(id), body);
  }

  static async extendTrial(
    id: string,
    body: { subjectType: BillingSubjectType; trialEndsAt: string; reason: string }
  ): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_SUBSCRIPTION_EXTEND_TRIAL(id), body);
  }

  static async grantPlan(body: {
    subjectType: BillingSubjectType;
    subjectId: string;
    planId: string;
    expiresAt?: string;
    reason: string;
  }): Promise<SubscriptionDto> {
    const r = await this.client.post(API_ENDPOINTS.ADMIN.BILLING_SUBSCRIPTION_GRANT, body);
    return r.data.data;
  }

  // ── Invoices ────────────────────────────────────────────────────────────────

  static async getInvoices(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    subjectType?: BillingSubjectType;
    search?: string;
  }): Promise<PaginatedResponse<InvoiceDto>> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_INVOICES, { params });
    return r.data.data;
  }

  static async createManualInvoice(body: {
    subjectType: BillingSubjectType;
    subjectId: string;
    items: Array<{ description: string; amount: number; quantity?: number }>;
    daysUntilDue?: number;
    currency?: string;
    sendViaProvider?: boolean;
    reason: string;
  }): Promise<InvoiceDto> {
    const r = await this.client.post(API_ENDPOINTS.ADMIN.BILLING_INVOICES, body);
    return r.data.data;
  }

  static async refundInvoice(
    id: string,
    amount: number | undefined,
    reason: string
  ): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_INVOICE_REFUND(id), { amount, reason });
  }

  static async markInvoicePaid(id: string, reason: string): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_INVOICE_MARK_PAID(id), { reason });
  }

  // ── Dunning ─────────────────────────────────────────────────────────────────

  static async getGstConfig(): Promise<GstSettings> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_GST_CONFIG);
    return r.data.data;
  }

  static async setGstConfig(config: GstSettings): Promise<GstSettings> {
    const r = await this.client.put(API_ENDPOINTS.ADMIN.BILLING_GST_CONFIG, config);
    return r.data.data;
  }

  static async getDunningConfig(): Promise<DunningConfig> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_DUNNING_CONFIG);
    return r.data.data;
  }

  static async setDunningConfig(config: DunningConfig): Promise<DunningConfig> {
    const r = await this.client.put(API_ENDPOINTS.ADMIN.BILLING_DUNNING_CONFIG, config);
    return r.data.data;
  }

  static async getFailedPayments(params?: {
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedResponse<InvoiceDto>> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_DUNNING_FAILED, { params });
    return r.data.data;
  }

  static async retryPayment(invoiceId: string): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_DUNNING_RETRY(invoiceId));
  }

  static async pauseDunning(invoiceId: string, reason: string): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_DUNNING_PAUSE(invoiceId), { reason });
  }

  static async resumeDunning(invoiceId: string): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_DUNNING_RESUME(invoiceId));
  }

  static async writeOffInvoice(invoiceId: string, reason: string): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_DUNNING_WRITE_OFF(invoiceId), { reason });
  }

  // ── Feature & entitlement controls ──────────────────────────────────────────

  static async getPlanMatrix(): Promise<SubscriptionPlanDto[]> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_PLAN_MATRIX);
    return r.data.data;
  }

  static async updatePlanLimits(
    planId: string,
    limits: PlanLimits,
    reason: string
  ): Promise<SubscriptionPlanDto> {
    const r = await this.client.patch(API_ENDPOINTS.ADMIN.BILLING_PLAN_LIMITS(planId), {
      limits,
      reason,
    });
    return r.data.data;
  }

  static async getEntitlements(
    subjectType: BillingSubjectType,
    subjectId: string
  ): Promise<EntitlementDto[]> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_ENTITLEMENTS, {
      params: { subjectType, subjectId },
    });
    return r.data.data;
  }

  static async grantEntitlement(body: {
    subjectType: BillingSubjectType;
    subjectId: string;
    featureKey: string;
    limit: number | null;
    expiresAt?: string;
    reason: string;
  }): Promise<EntitlementDto> {
    const r = await this.client.post(API_ENDPOINTS.ADMIN.BILLING_ENTITLEMENT_GRANT, body);
    return r.data.data;
  }

  static async revokeEntitlement(id: string, reason: string): Promise<void> {
    await this.client.post(API_ENDPOINTS.ADMIN.BILLING_ENTITLEMENT_REVOKE(id), { reason });
  }

  // ── Coupons ─────────────────────────────────────────────────────────────────

  static async getCoupons(): Promise<CouponDto[]> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_COUPONS);
    return r.data.data;
  }

  static async createCoupon(body: {
    code: string;
    description?: string;
    discountType: 'PERCENT' | 'FIXED_AMOUNT';
    discountValue: number;
    maxRedemptions?: number;
    expiresAt?: string;
    applicablePlanIds?: string[];
  }): Promise<CouponDto> {
    const r = await this.client.post(API_ENDPOINTS.ADMIN.BILLING_COUPONS, body);
    return r.data.data;
  }

  static async updateCoupon(id: string, body: { isActive?: boolean }): Promise<CouponDto> {
    const r = await this.client.patch(API_ENDPOINTS.ADMIN.BILLING_COUPON_UPDATE(id), body);
    return r.data.data;
  }

  // ── Reporting ───────────────────────────────────────────────────────────────

  static async getReportSummary(): Promise<BillingReportSummary> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_REPORT_SUMMARY);
    return r.data.data;
  }

  static async getCohorts(monthsBack = 12): Promise<CohortRetentionRow[]> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_REPORT_COHORTS, {
      params: { monthsBack },
    });
    return r.data.data;
  }

  static async getAging(): Promise<DelinquentAgingBucket[]> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_REPORT_AGING);
    return r.data.data;
  }

  static exportUrl(report: 'subscriptions' | 'invoices' | 'summary' | 'aging'): string {
    return `${API_ENDPOINTS.ADMIN.BILLING_REPORT_EXPORT}?report=${report}`;
  }

  static async downloadExport(
    report: 'subscriptions' | 'invoices' | 'summary' | 'aging'
  ): Promise<Blob> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_REPORT_EXPORT, {
      params: { report },
      responseType: 'blob',
    });
    return r.data;
  }

  static async getAuditLogs(params?: {
    page?: number;
    pageSize?: number;
    targetType?: string;
    targetId?: string;
    action?: string;
  }): Promise<PaginatedResponse<BillingAuditLogRow>> {
    const r = await this.client.get(API_ENDPOINTS.ADMIN.BILLING_AUDIT_LOGS, { params });
    return r.data.data;
  }
}
